import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, useFormikContext } from 'formik';
import { get } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import ThreeDotsMenu from '../../../../core/ThreeDotsMenu';
import { DB_FIELDS_VALID_VALUES } from '../../constants';
import GlobalFieldBadge from '../components/GlobalFieldBadge';
import ReorderDragHandle from '../components/ReorderDragHandle';
import copies from '../copies.json';
import FieldConfigurationCard from '../FieldConfigurationCard/FieldConfigurationCard';
import FieldGroupConfigurationCard, {
  FIELD_GROUPS_PATH,
  getNewFieldGroup,
  isUnsavedRow,
  MAX_FIELD_GROUPS,
} from '../FieldGroupConfigurationCard/FieldGroupConfigurationCard';
import MoveArrows from '../MoveArrows/MoveArrows';
import { isNewRow, markNewRow } from '../newRowIdentity';
import { reorderArrayField } from '../reorderArrayField';
import SortAzButton from '../SortAzButton/SortAzButton';

import { ADDITIONAL_INFORMATION_SECTIONS_PATH } from './AdditionalInformationSections/constants';
import BaseConfigurationSection from './BaseConfigurationSection';
import { partitionSectionIndexes } from './globalFieldEntries';

const SECTIONS_SPECIFIC_ID = 'configuration.incident_details.configured_fields';
const INCIDENT_DETAILS_SECTION = 'incident_details';
const SUPPLEMENTARY_INFO_SECTION = 'supplementary_info';
const TEXTS = copies.additional_information;
const FIELD_GROUP_TEXTS = copies.field_groups;
const CONFIGURED_FIELD_TEMPLATE = {
  id: '',
  desc: '',
  type: '',
  active: true,
  mandatory: false,
};

// "4 contact and 4 location fields": the cap on each slot-backed type is the number of db columns
// backing it, so the note is filled from that declaration rather than restating the count.
const SLOT_LIMITS = new Intl.ListFormat('en', { type: 'conjunction' }).format(
  Object.entries(DB_FIELDS_VALID_VALUES).map(
    ([type, slots]) => `${slots.length} ${copies.field_types.find((option) => option.id === type)?.desc.toLowerCase()}`
  )
);
const withSlotLimits = (subtitle) => subtitle?.replace('{slot_limits}', `${SLOT_LIMITS} fields`);

const getNewField = ({ index, section = undefined }) =>
  markNewRow({
    ...CONFIGURED_FIELD_TEMPLATE,
    id: `configured_field_${index}`,
    desc: `Configured Field ${index}`,
    section,
  });

// A missing section tag means the default Incident Details section.
const normalizeFieldSection = (fieldSection) => fieldSection || INCIDENT_DETAILS_SECTION;

// Move targets = the built-in sections plus every custom section, minus the field's current one.
// With no custom sections (e.g. auto) this degenerates to the single other built-in section,
// matching the previous binary toggle.
const getMoveTargets = ({ currentSection, customSections }) => {
  const builtIn = [
    { id: INCIDENT_DETAILS_SECTION, label: TEXTS.incident_details_section_name },
    { id: SUPPLEMENTARY_INFO_SECTION, label: TEXTS.supplementary_information_section_name },
  ];
  const custom = (customSections || []).map((section) => ({ id: section.id, label: section.desc || section.id }));
  return [...builtIn, ...custom].filter((target) => target.id !== normalizeFieldSection(currentSection));
};

const getMoveOptions = ({ fieldId, arrayIndex, configuredFields, customSections, setFieldValue }) =>
  getMoveTargets({ currentSection: configuredFields?.[arrayIndex]?.section, customSections }).map((target) => ({
    onClick: () => setFieldValue(`${fieldId}[${arrayIndex}].section`, target.id),
    node: TEXTS.move_to_section.replace('{section}', target.label),
    key: `moveSection_${target.id}`,
  }));

const ConfiguredFieldsSection = ({
  fieldId,
  section,
  disabled = false,
  disableCategories = false,
  hideCategories = false,
  subtitle = copies.additional_fields_note,
  disabledWithoutPadding = false,
  disabledWithoutChevron = false,
  withoutRequiredForClaimClosing = false,
  withoutRequired = false,
  withShowInInvolved = false,
  withIncludeInManagementPreview = false,
  requiredLabel,
  forceDisabled = false,
  excludeTypes = [],
  withDndReorder = false,
}) => {
  const { values, initialValues, setFieldValue } = useFormikContext();
  // The org being configured, not the admin's own — this screen is reached from the sysconfig context.
  const { organization } = useSysconfig();

  // Field groups sit beside the incident-details configured fields and, like them, carry a `section`
  // tag - so every mount over that flat array (Incident Details, Supplementary Information and each
  // custom section) offers them, and each renders only its own. Other configured-field lists (involved
  // parties, policy, document types) have no `field_groups` beside them. Auto is the exception: it
  // validates against `IncidentDetailsProductConfigBase`, which refuses a group that sets any section,
  // so there a group can only live in Incident Details.
  const isIncidentDetailsMount = section === INCIDENT_DETAILS_SECTION || !section;
  const withFieldGroups =
    fieldId === SECTIONS_SPECIFIC_ID &&
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS) &&
    (isIncidentDetailsMount || get(values, 'lob') !== 'auto_claim');
  const fieldGroups = get(values, FIELD_GROUPS_PATH) ?? [];
  const initialFieldGroups = get(initialValues, FIELD_GROUPS_PATH) ?? [];

  const initialConfiguredFields = get(initialValues, fieldId);
  const configuredFields = get(values, fieldId, []);
  const customSections = get(values, ADDITIONAL_INFORMATION_SECTIONS_PATH, []);

  // Which flat-array fields belong to THIS section: supplementary/incident are the built-ins
  // (incident also owns untagged fields); any other value is a custom section id matched exactly.
  const fieldBelongsToSection = (fieldSection) => {
    if (section === SUPPLEMENTARY_INFO_SECTION) return fieldSection === SUPPLEMENTARY_INFO_SECTION;
    if (isIncidentDetailsMount) {
      return fieldSection === INCIDENT_DETAILS_SECTION || !fieldSection;
    }
    return fieldSection === section;
  };

  const sectionIndexes = (configuredFields || [])
    .map((field, index) => (fieldBelongsToSection(field?.section) ? index : undefined))
    .filter((index) => index !== undefined);
  // Global fields render read-only at their anchors. Only this configuration's own fields are dragged,
  // sorted and moved, so no edit carries one past a global block.
  const { startIndexes, localIndexes, endIndexes } = partitionSectionIndexes(configuredFields || [], sectionIndexes);

  const configuredFieldIdToOriginalIndex = {};
  initialConfiguredFields?.forEach((field, index) => (configuredFieldIdToOriginalIndex[field.id] = index));

  // Same identity rule as the card's isNewConfiguredField: only an unsaved row may be removed, and
  // a row added in this session stays removable even after its key grows into a saved field's key.
  const isDeleteAllowed = (index) => {
    const currentField = configuredFields?.[index];
    return isNewRow(currentField) || !initialConfiguredFields?.find((f) => f.id === currentField?.id);
  };
  // Groups render in this section beside the flat fields, so they are counted with them: a
  // section holding one group and no fields is not "(0)". Gated on the same predicate that renders
  // the cards, so the count never names a group the section does not show.
  const groupsInSectionCount = withFieldGroups
    ? fieldGroups.filter((fieldGroup) => fieldBelongsToSection(fieldGroup?.section)).length
    : 0;
  const additionalFieldsCount = (sectionIndexes?.length || 0) + groupsInSectionCount;

  const renderGlobalFieldCards = (arrayIndexes) =>
    arrayIndexes.map((arrIdx) => (
      // Same row wrapper as this section's own rows, so the block is spaced like them.
      <div key={arrIdx} className={withDndReorder ? '[&>*>*]:!mb-0' : 'row-auto flex w-full justify-between'}>
        <FieldConfigurationCard
          configuredFieldIdToOriginalIndex={configuredFieldIdToOriginalIndex}
          fieldIdParentArray={fieldId}
          fieldId={`${fieldId}[${arrIdx}]`}
          isConfiguredField
          withoutRequiredForClaimClosing={withoutRequiredForClaimClosing}
          withoutRequired={withoutRequired}
          withShowInInvolved={withShowInInvolved}
          withIncludeInManagementPreview={withIncludeInManagementPreview}
          requiredLabel={requiredLabel}
          excludeTypes={excludeTypes}
          withDndReorder={withDndReorder}
          readOnly
          actions={<GlobalFieldBadge />}
          disableCategories={disableCategories}
          hideCategories={hideCategories}
        />
      </div>
    ));

  const renderFieldGroupCards = (fieldGroupHelpers) =>
    fieldGroups.map((fieldGroup, groupIndex) => {
      // Filtered in place rather than beforehand, so a card keeps its index into the whole array.
      if (!fieldBelongsToSection(fieldGroup?.section)) {
        return null;
      }
      // A saved group cannot be removed at all: the shared config-diff guard refuses any removal
      // from a saved configuration, so a saved group is retired with `active: false` instead.
      const isRemovable = isUnsavedRow(fieldGroup, initialFieldGroups);
      return (
        <FieldGroupConfigurationCard
          key={groupIndex}
          index={groupIndex}
          disabled={disabled || forceDisabled}
          actions={
            <div className="flex items-center justify-center leading-[0]">
              <ThreeDotsMenu
                stopPropagation
                disabled={forceDisabled || !isRemovable}
                options={[
                  {
                    onClick: () => fieldGroupHelpers.remove(groupIndex),
                    node: FIELD_GROUP_TEXTS.remove,
                    key: 'remove',
                    disabled: !isRemovable,
                  },
                ]}
              />
            </div>
          }
        />
      );
    });

  const renderAddFieldGroupButton = (fieldGroupHelpers) => {
    const atCap = fieldGroups.length >= MAX_FIELD_GROUPS;
    const addButton = (
      <Button
        variant="text"
        color="primary"
        // An absent `section` IS the default Incident Details placement (see
        // `normalizeFieldSection` above and `FieldGroup.section`), so tag the group only when it
        // is somewhere else. Sending the literal 'incident_details' made every save fail on any
        // line of business but general claim: `IncidentDetailsProductConfigBase` refuses a group
        // that sets a section at all, and auto is the line this feature exists for.
        onClick={() =>
          fieldGroupHelpers.push(
            getNewFieldGroup(fieldGroups.length + 1, section === INCIDENT_DETAILS_SECTION ? undefined : section)
          )
        }
        disabled={disabled || forceDisabled || atCap}
      >
        <AddIcon />
        {FIELD_GROUP_TEXTS.add_field_group}
      </Button>
    );

    if (!atCap) {
      return addButton;
    }

    return (
      <FsTooltip
        arrow
        title={FIELD_GROUP_TEXTS.add_field_group_at_cap_tooltip.replace('{max}', String(MAX_FIELD_GROUPS))}
      >
        {/* span keeps the tooltip working over a disabled button, which swallows pointer events */}
        <span>{addButton}</span>
      </FsTooltip>
    );
  };

  // The group cards plus the action row. Only this much sits inside the groups' own FieldArray,
  // so the configured-field rows above it are left exactly as they were.
  const renderFieldGroupsAndActions = (arrayHelpers, fieldGroupHelpers) => (
    <>
      {withFieldGroups ? renderFieldGroupCards(fieldGroupHelpers) : null}
      {withFieldGroups ? <ErrorHelperTextFormik id={FIELD_GROUPS_PATH} withoutChildren /> : null}
      <div className="flex">
        <Button
          variant="text"
          color="primary"
          onClick={() => arrayHelpers.push(getNewField({ index: configuredFields?.length + 1, section }))}
          disabled={disabled || forceDisabled}
        >
          <AddIcon />
          ADD AN ADDITIONAL FIELD
        </Button>
        {withFieldGroups ? renderAddFieldGroupButton(fieldGroupHelpers) : null}
      </div>
    </>
  );

  return (
    <BaseConfigurationSection
      title={`Additional Fields (${additionalFieldsCount})`}
      subtitle={withSlotLimits(subtitle)}
      noGridContainer
      disabled={disabled}
      withActionsContainerFullWidth={false}
      disabledWithoutPadding={disabledWithoutPadding}
      disabledWithoutChevron={disabledWithoutChevron}
      // Keep A-Z sorting available alongside drag reordering, matching the incident-types list.
      // Scope the sort to this section's rows so it doesn't reorder the other section's fields,
      // which share the same configured_fields array.
      actions={
        withDndReorder ? (
          // The shared actions container centers vertically; with this section's two-line
          // subtitle that dropped the sort button to the middle of the header. Pin it to the
          // top so it lines up with the "Additional Fields" title, matching the incident-types
          // section (which has no subtitle, so it never drifted).
          <div className="flex self-start">
            <SortAzButton
              fieldId={fieldId}
              sortKey="desc"
              sortIndexes={localIndexes}
              disabled={disabled || forceDisabled}
            />
          </div>
        ) : undefined
      }
    >
      <FieldArray
        name={fieldId}
        render={(arrayHelpers) => (
          <div className="mr-6 flex flex-col gap-12">
            {renderGlobalFieldCards(startIndexes)}
            {withDndReorder ? (
              <DndOrderedList
                className="flex flex-col gap-12"
                // The draggable row is a flex item, so the card's own bottom margin (CollapsibleWrapper
                // sets margin-bottom) is trapped inside the row and renders as a white strip under the
                // card in the drag preview. Row spacing comes from the list gap, so zero that margin.
                itemClassName="[&>*>*]:!mb-0"
                disabled={disabled || forceDisabled}
                items={localIndexes.map((arrIdx) => ({ arrIdx }))}
                // Key on the array position, not the field's `id`. Field ids are user-editable
                // (Display Name syncs to Key via snakeCase), so an id-derived key changes mid-edit
                // and remounts the row, dropping input focus. The array index is stable while typing.
                getItemId={({ arrIdx }) => String(arrIdx)}
                onReorder={({ fromIndex, toIndex }) =>
                  reorderArrayField(arrayHelpers, localIndexes[fromIndex], localIndexes[toIndex])
                }
                renderItem={({ arrIdx }, { dragHandleProps }) => (
                  <FieldConfigurationCard
                    configuredFieldIdToOriginalIndex={configuredFieldIdToOriginalIndex}
                    fieldIdParentArray={fieldId}
                    fieldId={`${fieldId}[${arrIdx}]`}
                    isConfiguredField
                    withoutRequiredForClaimClosing={withoutRequiredForClaimClosing}
                    withoutRequired={withoutRequired}
                    withShowInInvolved={withShowInInvolved}
                    withIncludeInManagementPreview={withIncludeInManagementPreview}
                    requiredLabel={requiredLabel}
                    forceDisabled={forceDisabled}
                    excludeTypes={excludeTypes}
                    withDndReorder
                    actions={
                      // leading-[0] collapses the inline line-box so the ThreeDotsMenu's
                      // baseline-aligned icon centers with the flex-centered drag grip.
                      <div className="flex items-center justify-center leading-[0]">
                        <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={disabled || forceDisabled} />
                        <ThreeDotsMenu
                          stopPropagation
                          disabled={forceDisabled || (fieldId !== SECTIONS_SPECIFIC_ID && !isDeleteAllowed(arrIdx))}
                          options={[
                            ...(fieldId !== SECTIONS_SPECIFIC_ID
                              ? []
                              : getMoveOptions({
                                  fieldId,
                                  arrayIndex: arrIdx,
                                  configuredFields,
                                  customSections,
                                  setFieldValue,
                                })),
                            {
                              onClick: () => arrayHelpers.remove(arrIdx),
                              node: 'Remove',
                              key: 'remove',
                              disabled: !isDeleteAllowed(arrIdx),
                            },
                          ]}
                        />
                      </div>
                    }
                    disableCategories={disableCategories}
                    hideCategories={hideCategories}
                  />
                )}
              />
            ) : (
              localIndexes.map((currentArrayIndex, idx) => (
                <div className="row-auto flex w-full justify-between" key={currentArrayIndex}>
                  <FieldConfigurationCard
                    configuredFieldIdToOriginalIndex={configuredFieldIdToOriginalIndex}
                    fieldIdParentArray={fieldId}
                    fieldId={`${fieldId}[${currentArrayIndex}]`}
                    isConfiguredField
                    withoutRequiredForClaimClosing={withoutRequiredForClaimClosing}
                    withoutRequired={withoutRequired}
                    withShowInInvolved={withShowInInvolved}
                    withIncludeInManagementPreview={withIncludeInManagementPreview}
                    requiredLabel={requiredLabel}
                    forceDisabled={forceDisabled}
                    excludeTypes={excludeTypes}
                    actions={
                      <div className="flex items-center justify-center">
                        <MoveArrows
                          onMoveUp={() => {
                            arrayHelpers.move(currentArrayIndex, localIndexes[Math.max(idx - 1, 0)]);
                          }}
                          onMoveDown={() => {
                            arrayHelpers.move(
                              currentArrayIndex,
                              localIndexes[Math.min(idx + 1, localIndexes.length - 1)]
                            );
                          }}
                          disabled={disabled || forceDisabled}
                        />
                        <ThreeDotsMenu
                          stopPropagation
                          disabled={
                            forceDisabled || (fieldId !== SECTIONS_SPECIFIC_ID && !isDeleteAllowed(currentArrayIndex))
                          }
                          options={[
                            ...(fieldId !== SECTIONS_SPECIFIC_ID
                              ? []
                              : getMoveOptions({
                                  fieldId,
                                  arrayIndex: currentArrayIndex,
                                  configuredFields,
                                  customSections,
                                  setFieldValue,
                                })),
                            {
                              onClick: () => arrayHelpers.remove(currentArrayIndex),
                              node: 'Remove',
                              key: 'remove',
                              disabled: !isDeleteAllowed(currentArrayIndex),
                            },
                          ]}
                        />
                      </div>
                    }
                    disableCategories={disableCategories}
                    hideCategories={hideCategories}
                  />
                </div>
              ))
            )}
            {renderGlobalFieldCards(endIndexes)}
            <ErrorHelperTextFormik id={fieldId} withoutChildren />
            {/* The groups are a second array on the same configuration, so they need their own
                FieldArray — nested here, around just the cards and the action row, so both ADD
                actions share one row. Only the mount that owns the path registers it. */}
            {withFieldGroups ? (
              <FieldArray
                name={FIELD_GROUPS_PATH}
                render={(fieldGroupHelpers) => renderFieldGroupsAndActions(arrayHelpers, fieldGroupHelpers)}
              />
            ) : (
              renderFieldGroupsAndActions(arrayHelpers, null)
            )}
          </div>
        )}
      />
    </BaseConfigurationSection>
  );
};

ConfiguredFieldsSection.propTypes = {
  fieldId: PropTypes.string.isRequired,
  section: PropTypes.string,
  disabled: PropTypes.bool,
  disableCategories: PropTypes.bool,
  hideCategories: PropTypes.bool,
  subtitle: PropTypes.string,
  disabledWithoutPadding: PropTypes.bool,
  disabledWithoutChevron: PropTypes.bool,
  withoutRequiredForClaimClosing: PropTypes.bool,
  withoutRequired: PropTypes.bool,
  withShowInInvolved: PropTypes.bool,
  withIncludeInManagementPreview: PropTypes.bool,
  requiredLabel: PropTypes.string,
  forceDisabled: PropTypes.bool,
  excludeTypes: PropTypes.arrayOf(PropTypes.string),
  withDndReorder: PropTypes.bool,
};

export default ConfiguredFieldsSection;
export { getMoveOptions, getMoveTargets };
